import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { api, type Me } from "./api";
import Alerts from "./pages/Alerts";
import Billing from "./pages/Billing";
import Channels from "./pages/Channels";
import DeployWizard from "./pages/DeployWizard";
import EndpointDetail from "./pages/EndpointDetail";
import Fleet from "./pages/Fleet";
import InvoiceDetail from "./pages/InvoiceDetail";
import Login from "./pages/Login";
import OriginHealth from "./pages/OriginHealth";
import Overview from "./pages/Overview";
import Releases from "./pages/Releases";
import Resellers from "./pages/Resellers";
import Sites from "./pages/Sites";
import Users from "./pages/Users";
import { Spinner, ToastHost } from "./ui";

interface NavItem {
  to: string;
  label: string;
  roles?: string[];
}

const NAV: NavItem[] = [
  { to: "/overview", label: "Overview" },
  { to: "/fleet", label: "Fleet" },
  { to: "/sites", label: "Sites" },
  { to: "/deploy", label: "Deploy", roles: ["nci_admin", "nci_ops"] },
  { to: "/channels", label: "Channels", roles: ["nci_admin", "nci_ops"] },
  { to: "/origin", label: "Origin health", roles: ["nci_admin", "nci_ops"] },
  { to: "/releases", label: "Releases", roles: ["nci_admin", "nci_ops"] },
  { to: "/alerts", label: "Alerts" },
  { to: "/billing", label: "Billing" },
  { to: "/resellers", label: "Resellers", roles: ["nci_admin"] },
  // Same page, two names: NCI staff are administering everybody's accounts,
  // a reseller is looking after their own people.
  { to: "/users", label: "Users", roles: ["nci_admin", "nci_ops"] },
  { to: "/users", label: "Team", roles: ["reseller"] },
];

export default function App() {
  const { data: me, isLoading, isError } = useQuery<Me>({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/v1/auth/me"),
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label="Signing in…" />
      </div>
    );
  }
  if (isError || !me) return <Login />;

  return (
    <ToastHost>
      <div className="flex min-h-full flex-col">
        <Chrome me={me} />
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-6 py-6">
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<Overview me={me} />} />
            <Route path="/fleet" element={<Fleet me={me} />} />
            <Route path="/endpoints/:id" element={<EndpointDetail me={me} />} />
            <Route path="/sites" element={<Sites me={me} />} />
            <Route path="/deploy" element={<DeployWizard me={me} />} />
            <Route path="/deploy/:jobId" element={<DeployWizard me={me} />} />
            <Route path="/channels" element={<Channels me={me} />} />
            <Route path="/origin" element={<OriginHealth />} />
            <Route path="/releases" element={<Releases me={me} />} />
            <Route path="/alerts" element={<Alerts me={me} />} />
            <Route path="/billing" element={<Billing me={me} />} />
            <Route path="/invoices/:id" element={<InvoiceDetail me={me} />} />
            <Route path="/resellers" element={<Resellers me={me} />} />
            <Route path="/users" element={<Users me={me} />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </main>
        <footer className="border-t border-ink-700 px-6 py-3 text-xs text-ink-400">
          Outpost · NetComTV distribution network · all times America/Chicago
        </footer>
      </div>
    </ToastHost>
  );
}

function Chrome({ me }: { me: Me }) {
  const location = useLocation();
  const visible = NAV.filter((item) => !item.roles || item.roles.includes(me.role));

  const { data: alerts } = useQuery({
    queryKey: ["alerts", "count"],
    queryFn: () => api.get<{ total: number }>("/v1/alerts?limit=1"),
    refetchInterval: 60_000,
  });

  return (
    <header className="sticky top-0 z-40 border-b border-ink-700 bg-ink-900/95 backdrop-blur">
      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-6 px-6 py-3">
        <NavLink to="/fleet" className="flex shrink-0 items-center gap-3">
          <img src="/brand/netcomtv-logo-white.svg" alt="NetComTV" className="h-5 w-auto" />
          <span className="border-l border-ink-600 pl-3 text-eyebrow font-semibold uppercase text-brand-on">
            Outpost
          </span>
        </NavLink>

        <nav className="flex flex-1 flex-wrap items-center gap-1">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  "border-b-2 px-3 py-2 text-sm font-semibold transition-colors",
                  isActive || location.pathname.startsWith(item.to)
                    ? "border-brand text-ink-100"
                    : "border-transparent text-ink-400 hover:border-ink-500 hover:text-ink-100",
                )
              }
            >
              {item.label}
              {item.to === "/alerts" && alerts && alerts.total > 0 && (
                <span className="ml-2 rounded-full bg-state-crit/20 px-1.5 py-0.5 text-xs text-state-crit">
                  {alerts.total}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3 text-sm">
          <div className="text-right">
            <div className="text-ink-200">{me.full_name || me.email}</div>
            <div className="text-xs text-ink-400">
              {me.role === "reseller" ? (me.reseller_name ?? "Reseller") : me.role.replace("_", " ")}
            </div>
          </div>
          <button
            className="btn-secondary"
            onClick={async () => {
              await api.post("/v1/auth/logout");
              window.location.reload();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
